import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@ohif/ui';
import { Table } from '../../../shared/custom-ui';
import { V2Button } from '../../../shared/custom-ui/v2-components';
import TableSectionSkeleton from '../../../shared/custom-ui/skeleton/TableSectionSkeleton/TableSectionSkeleton';
import ApiServices from '../../../shared/ApiServices/ApiServices';
import MedicalNotesDialog from '../../MedicalNotes/MedicalNotesDialog';
import { notesColumns } from './notesColumns';
import { useClickOutside } from '../../../hooks/useClickOutside/useClickOutside';
import UnifiedPagination from '../../../shared/custom-ui/UnifiedPagination';

export default function Notes() {
    const { Patientid } = useParams();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState({ id: null, text: '' });
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const notesPerPage = 10;

    const dropdownRef = useRef(null);
    useClickOutside(dropdownRef, () => setDropdownOpen({}));

    const fetchNotes = async () => {
        setLoading(true);
        try {
            const response = await ApiServices.getNotes(Patientid);
            setNotes(response.notes || []);
        } catch {
            toast.error('Failed to fetch notes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Patientid) fetchNotes();
    }, [Patientid]);

    const openNotesDialog = () => setNotesDialogOpen(true);
    const closeDialogs = () => {
        setNotesDialogOpen(false);
        setEditDialogOpen(false);
        setSelectedNote({ id: null, text: '' });
    };

    const openEditDialog = (noteId, noteText) => {
        setSelectedNote({ id: noteId, text: noteText });
        setEditDialogOpen(true);
    };

    const handleDelete = async (noteId) => {
        try {
            await ApiServices.deleteNote(Patientid, noteId);
            toast.success('Note deleted successfully.');
            fetchNotes();
        } catch {
            toast.error('Failed to delete note.');
        }
    };

    const toggleDropdown = noteId => {
        setDropdownOpen(prev => ({
            ...prev,
            [noteId]: !prev[noteId],
        }));
    };

    const indexOfLastNote = currentPage * notesPerPage;
    const indexOfFirstNote = indexOfLastNote - notesPerPage;
    const currentNotes = notes.slice(indexOfFirstNote, indexOfLastNote);
    const totalPages = Math.ceil(notes.length / notesPerPage);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const customColumns = notesColumns.map(col =>
        col.key === 'index'
            ? {
                ...col,
                formatter: (_: any, __: any, rowIndex: number) => indexOfFirstNote + rowIndex + 1,
            }
            : col.isAction
                ? {
                    ...col,
                    formatter: (_: any, row) => {
                        const isOpen = dropdownOpen[row.SK];
                        return (
                            <div className="relative inline-block text-left">
                                <button
                                    type="button"
                                    onClick={() => toggleDropdown(row.SK)}
                                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
                                >
                                    <Icon name="kebab-vertical" className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                                </button>
                                {isOpen && (
                                    <div
                                        ref={dropdownRef}
                                        className="absolute right-0 top-full z-50 w-32 rounded-md border shadow-lg"
                                        style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border-subtle)' }}
                                    >
                                        <ul className="py-1">
                                            <li>
                                                <button
                                                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => openEditDialog(row.SK, row.Note)}
                                                >
                                                    Edit
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100"
                                                    onClick={() => handleDelete(row.SK)}
                                                >
                                                    Delete
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    }
                }
                : col
    );

    const tableData = loading
        ? []
        : currentNotes.length > 0
            ? currentNotes
            : [{ emptyRow: true }];

    if (loading) {
        return <TableSectionSkeleton rows={5} columns={4} showActionButton />;
    }

    return (
        <section className="flex h-full flex-col rounded-lg bg-gradient-to-b from-blue-50 to-gray-50 shadow-lg">
            <div className="sticky top-0 z-10 border-b px-6 py-4 rounded-t-[var(--radius-lg)] flex items-center justify-between" style={{ background: 'var(--primary-25)', borderColor: 'var(--border-subtle)' }}>
                <span className="text-lg font-semibold text-blue-900">Notes</span>
                <V2Button
                    variant="primary"
                    size="small"
                    onClick={openNotesDialog}
                    className="flex items-center"
                    leftIcon={<Icon name="add-plus-icon" className="h-4 w-4 md:mr-1" />}
                >
                    <span className="hidden md:inline text-sm font-medium">Add Note</span>
                </V2Button>
            </div>

            <div
                className="relative flex-1 px-6 py-4 overflow-y-auto custom-scrollbar max-h-[calc(100vh-464px)]"
                style={{ background: 'var(--bg-card)' }}
            >
                <Table
                    columns={customColumns}
                    data={currentNotes}
                    rowKey={(row, index) => row.SK || index}
                    stickyHeader={false}
                    emptyText="No notes available."
                />

                {/* Pagination */}
                {notes.length > 0 && (
                    <div className="pt-4">
                        <UnifiedPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={paginate}
                        />
                    </div>
                )}

            </div>

            <MedicalNotesDialog
                isOpen={notesDialogOpen || editDialogOpen}
                mode={editDialogOpen ? 'edit' : 'create'}
                patientId={Patientid}
                existingNote={editDialogOpen ? selectedNote : null}
                onClose={closeDialogs}
                onSuccess={fetchNotes}
            />
        </section>
    );
}
